import React, { useMemo } from 'react';
import { Field, Form, Formik, type FormikErrors } from 'formik';
import { ModalContent, ModalText, ModalWrapper } from './styled';
import { CloseButton } from 'components/Modal/styled';
import Icon from 'components/icons';
import { hideModal } from 'store/modal/actions';
import { useDispatch, useSelector } from 'react-redux';
import { InlineButton, PrimaryLoadingButton } from 'components/Button';
import FormikTextInput from 'components/Forms/FormikTextInput';
import { isValidEmail } from 'helpers/strings';
import itly from 'itly';
import { customerSelector } from 'store/customer/selectors';
import { Product } from 'types/Product';
import { useMedia } from 'features/pdp/hooks/useMedia';
import { renderInfoToast } from 'components/InfoToast';
import { getProductUrlForOptions } from 'features/pdp/helpers';
import { analyticsIdentifyCustomer } from 'analytics/identifyProviders';
import { useProductForm } from 'features/pdp/hooks/useProductForm';
import { sendDesignOnDesktopEmail } from 'services/magenum/email';
import { useProductLivePreview } from 'features/pdp/hooks/useProductLivePreview';

const IvalidEmailError = 'Use a valid email like name@example.com';
const SucessToastTitle = 'Check your inbox';
const SucessToastBody = 'We sent a link to continue this project in the desktop editor.';

interface ModalProps {
  email: string;
  product: Product;
  onContinueOnMobile: () => void;
}

interface ModalFields {
  email: string;
}

const DesignOnDesktopModal = ({product, onContinueOnMobile, ...initialValues}: ModalProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const customer = useSelector(customerSelector);
  const { form } = useProductForm();
  const livePreviewUrl = useProductLivePreview();

  const { photos } = useMedia();
  const pdpImageUrl = useMemo(() => livePreviewUrl || (photos && photos[0].thumb), [livePreviewUrl, photos]);

  const handleClose = () => {
    dispatch(hideModal());
  };

  const handleContinueOnMobile = () => {
    onContinueOnMobile();
    handleClose();
  };

  const validateModal = (fields: ModalFields): FormikErrors<ModalFields> => {
    return {...(!isValidEmail(fields.email) && { email: IvalidEmailError })};
  };

  const handleSubmit = (fields: ModalFields) => {
    const customerId = customer?.id?.toString() ?? '';
    const pdpUrl = getProductUrlForOptions(product, Object.values(form.getValues()));

    itly.designItLaterEmailSubmitted({
      customer_id: customerId,
      email: fields.email,
      subscription: 'subscribed',
      location: 'Mobile PDP pop up',
      last_updated: new Date().toISOString(),
      pdp_url: pdpUrl,
      pdp_image: pdpImageUrl
    });

    const customerData = {
      firstname: customer?.firstname ?? '',
      lastname: customer?.lastname ?? '',
      email: fields.email
    };

    analyticsIdentifyCustomer(customerData);
    
    sendDesignOnDesktopEmail({
      email: fields.email,
      pdp: pdpUrl,
      pdpImage: pdpImageUrl
    });
    
    handleClose();
    renderInfoToast(SucessToastTitle, SucessToastBody);
  };

  return (
    <ModalWrapper>
      <CloseButton onClick={handleClose}>
        <Icon icon="close" size="small" />
      </CloseButton>

      <ModalContent>
        <ModalText>
          <h5>Start Designing on Desktop</h5>
          <p>Get a link to continue this project in the desktop editor - larger screen, easier editing, and advanced design tools.</p>
        </ModalText>

        <Formik 
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validateModal}
          render={({ isSubmitting }) => (
            <Form>
              <Field
                label="Email"
                name="email"
                component={FormikTextInput}
                placeholder="name@example.com"
                showErrorIcon
              />

              <PrimaryLoadingButton isLoading={isSubmitting} type="submit" id="email-me">
                Email me a link
              </PrimaryLoadingButton>

              <InlineButton id="design-services-button" onClick={handleContinueOnMobile}>
                Continue on mobile →
              </InlineButton>
            </Form>
          )}
        />
      </ModalContent>
    </ModalWrapper>
  );
};

export default DesignOnDesktopModal;
